import React, { useEffect, useMemo, useState } from 'react';
import { Download, Plus, Trash2 } from 'lucide-react';
import { useTheme, RADIUS, TYPE } from '../theme';
import { Badge, Button, Card, Icon, IconButton, Segmented } from '../components/ui';
import {
  huntApi, kr, chargeStatusText, filterCharges, perHunter, downloadText, notifyHuntEconomyChanged, deleteChargeConfirmText, GAME_OPTIONS, HUNT_ECONOMY_CHANGED,
  type ForbiddenRule, type HuntCharge, type HuntEconomy,
} from '../huntEconomy';

// ─────────────────────────────────────────────────────────────────
// P5 · Ekonomi-fliken: avgifter efter jakt per jägare och jakt – betalt/obetalt, påminnelser,
// manuell markering (kontant/Swish), stryka, export (CSV + bokföringsunderlag). Utbetalning till
// markägaren via Stripe Connect visas bara som status (D4) – inga egna utbetalningar här.
// A6 · Ej tillåtet vilt och bot per viltslag (ForbiddenGamePanel).
// ─────────────────────────────────────────────────────────────────

type Filter = 'alla' | 'obetalda' | 'betalda' | 'strukna';

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'danger' | 'ok' }) {
  const T = useTheme();
  return (
    <div style={{ padding: '10px 12px', borderRadius: RADIUS.md, background: T.surface2, minWidth: 0 }}>
      <div style={{ fontSize: 12, color: T.textSub, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: tone === 'danger' ? T.danger : tone === 'ok' ? T.ok : T.text }}>{value}</div>
    </div>
  );
}

function ChargeRow({ c, onChanged }: { c: HuntCharge; onChanged: (c: HuntCharge) => void }) {
  const T = useTheme();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [paying, setPaying] = useState(false);
  const st = chargeStatusText(c);
  const run = async (fn: () => Promise<HuntCharge | boolean>, done?: string) => {
    setBusy(true); setMsg('');
    try {
      const r = await fn();
      if (typeof r === 'object') { onChanged(r); notifyHuntEconomyChanged(); }
      if (done) setMsg(done);
    } catch (e: any) { setMsg(e.message || 'Något gick fel'); }
    setBusy(false);
  };
  const spec = c.lines.filter(l => l.kind !== 'info').map(l => `${l.antal > 1 ? `${l.antal} × ` : ''}${l.viltslag}${l.kind === 'bot' ? ' (bot)' : ''}`).join(', ');
  return (
    <div data-testid="charge-row" style={{ borderTop: `1px solid ${T.border}`, padding: '12px 0', display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, flex: '1 1 160px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.hunterName || c.hunterEmail}</span>
        <span style={{ fontWeight: 800 }}>{kr(c.totalKr)}</span>
        <Badge tone={st.tone}>{st.label}</Badge>
      </div>
      <div style={{ fontSize: 13, color: T.textSub }}>
        {c.huntDate} · {c.huntTitle || 'Jakt'}{spec ? ` · ${spec}` : ''}{c.preliminary ? ' · kött efter vägning' : ''}
        {c.status === 'unpaid' && c.dueDate ? ` · förfaller ${c.dueDate}` : ''}{c.reminderCount ? ` · ${c.reminderCount} påminnelse${c.reminderCount > 1 ? 'r' : ''}` : ''}
        {c.paidNote ? ` · ${c.paidNote}` : ''}
      </div>
      {paying
        ? <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 14 }}>Betald med:</span>
          {(['swish', 'kontant', 'annat'] as const).map(m => (
            <Button key={m} size="sm" variant="secondary" disabled={busy} onClick={() => { setPaying(false); void run(() => huntApi.markHuntChargePaid(c.id, m)); }}>{m === 'swish' ? 'Swish' : m === 'kontant' ? 'Kontant' : 'Annat'}</Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setPaying(false)}>Avbryt</Button>
        </div>
        : <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {c.status === 'unpaid' && <>
            <Button size="sm" disabled={busy} onClick={() => setPaying(true)}>Markera betald</Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => void run(() => huntApi.remindHuntCharge(c.id), 'Påminnelse skickad')}>Påminn</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => {
              const note = prompt(`Stryka ${kr(c.totalKr)} för ${c.hunterName || c.hunterEmail}? Skriv gärna varför:`, '');
              if (note !== null) void run(() => huntApi.voidHuntCharge(c.id, note));
            }}>Stryk</Button>
          </>}
          {((c.status === 'paid' && c.paidMethod !== 'stripe') || c.status === 'void') && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => { if (confirm('Ångra och sätt som obetald igen?')) void run(() => huntApi.markHuntChargeUnpaid(c.id)); }}>Ångra</Button>
          )}
          {/* EJ2: ta bort raden i vilken status som helst – Stripe rörs inte. */}
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => { if (confirm(deleteChargeConfirmText(c))) void run(() => huntApi.deleteHuntCharge(c.id).then(ok => { notifyHuntEconomyChanged(); return ok; })); }}><Icon as={Trash2} size={16} /> Ta bort</Button>
        </div>}
      {msg && <div role="status" style={{ fontSize: 13, color: T.textSub }}>{msg}</div>}
    </div>
  );
}

export function HuntPayments({ markId, markName }: { markId: string; markName: string }) {
  const T = useTheme();
  const [list, setList] = useState<HuntCharge[] | null>(null);
  const [eco, setEco] = useState<HuntEconomy | null>(null);
  const [filter, setFilter] = useState<Filter>('obetalda');
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [remindingAll, setRemindingAll] = useState(false);
  const load = () => Promise.all([huntApi.markHuntCharges(markId), huntApi.markHuntEconomy(markId)])
    .then(([l, e]) => { setList(l); setEco(e); setErr(''); })
    .catch(e => { setErr(e.message); setList([]); });
  useEffect(() => { void load(); }, [markId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const on = () => void load();
    window.addEventListener(HUNT_ECONOMY_CHANGED, on);
    return () => window.removeEventListener(HUNT_ECONOMY_CHANGED, on);
  }, [markId]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => (list ? filterCharges(list, { status: filter, q }) : []), [list, filter, q]);
  const hunters = useMemo(() => (list ? perHunter(list) : []), [list]);
  const unpaid = (list ?? []).filter(c => c.status === 'unpaid');
  const exportCsv = async (format: 'lista' | 'bokforing') => {
    try {
      const csv = await huntApi.markHuntChargesCsv(markId, format);
      const date = new Date().toISOString().slice(0, 10);
      downloadText(`${format === 'lista' ? 'fallavgifter' : 'bokforingsunderlag'}-${markName.replace(/[^\p{L}\p{N}]+/gu, '-')}-${date}.csv`, csv);
    } catch (e: any) { setErr(e.message); }
  };
  const remindAll = async () => {
    if (!confirm(`Skicka påminnelse till ${unpaid.length} jägare med obetalda avgifter?`)) return;
    setRemindingAll(true);
    for (const c of unpaid) await huntApi.remindHuntCharge(c.id).catch(() => {});
    setRemindingAll(false);
    void load();
  };

  if (!list) return null;
  return (
    <Card data-testid="hunt-payments" style={{ marginBottom: 24, display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 14 }}>
      <div>
        <div style={{ ...TYPE.title }}>Betalningar efter jakt</div>
        <div style={{ ...TYPE.small, color: T.textSub }}>Fällavgifter och böter per jägare och jakt, när jaktledaren bekräftat det fällda viltet.</div>
      </div>
      {err && <div role="alert" style={{ color: T.danger }}>{err}</div>}
      {eco && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 8 }}>
          <Stat label="Obetalt" value={kr(eco.unpaidKr)} tone={eco.unpaidKr ? 'danger' : undefined} />
          <Stat label="Varav förfallet" value={kr(eco.overdueKr)} tone={eco.overdueKr ? 'danger' : undefined} />
          <Stat label="Betalt" value={kr(eco.paidKr)} tone="ok" />
          <Stat label="Rapporter att bekräfta" value={String(eco.pendingReports)} />
        </div>
      )}
      {eco && (
        <div data-testid="payout-status" style={{ fontSize: 13, color: T.textSub, padding: '10px 12px', borderRadius: RADIUS.md, border: `1px dashed ${T.borderHover}` }}>
          <strong style={{ color: T.text }}>Utbetalning till markägaren (Stripe Connect): </strong>
          {eco.connect.needsReconnect ? 'Stripe har gått över till skarpt läge – koppla ditt konto igen under Stripe ovan. Tills dess kan jägarna bara betala med Swish/kontant.'
            : !eco.connect.connected ? 'inget Stripe-konto kopplat – jägarna kan bara betala med Swish/kontant. Koppla kontot under Stripe ovan.'
            : !eco.connect.chargesEnabled ? 'kontot är kopplat men inte aktiverat för kortbetalning än.'
              : <>kortbetalningar går direkt till markägarens Stripe-konto minus plattformsavgiften. Hittills {kr(eco.stripeKr)} med kort, varav avgift {kr(eco.platformFeeKr)} → {kr(eco.toOwnerViaStripeKr)} till markägaren{eco.connect.payoutsEnabled ? ', betalas ut av Stripe enligt kontots schema' : ' (utbetalningar inte aktiverade i Stripe än)'}. Swish/kontant ({kr(eco.manualKr)}) går direkt till jaktledaren.</>}
        </div>
      )}
      {hunters.length > 0 && (
        <details>
          <summary style={{ cursor: 'pointer', fontWeight: 700, minHeight: 44, display: 'flex', alignItems: 'center' }}>Per jägare ({hunters.length})</summary>
          <div style={{ display: 'grid', gap: 4 }}>
            {hunters.map(h => (
              <div key={h.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 14, flexWrap: 'wrap' }}>
                <span>{h.name} <span style={{ color: T.textMuted }}>({h.count} jakter)</span></span>
                <span><span style={{ color: T.ok }}>{kr(h.paidKr)} betalt</span>{h.unpaidKr ? <span style={{ color: T.danger, fontWeight: 700 }}> · {kr(h.unpaidKr)} obetalt</span> : null}</span>
              </div>
            ))}
          </div>
        </details>
      )}
      {/* Fyra segment blir bredare än kortet på 375 px – raden scrollar då i sidled i stället för att sticka ut. */}
      <div style={{ overflowX: 'auto', minWidth: 0 }}>
        <Segmented label="Visa" size="sm" block={false} value={filter} onChange={setFilter} options={[
          { value: 'obetalda', label: `Obetalda (${unpaid.length})` }, { value: 'betalda', label: 'Betalda' }, { value: 'alla', label: 'Alla' }, { value: 'strukna', label: 'Strukna' },
        ]} />
      </div>
      <input aria-label="Sök jägare eller jakt" placeholder="Sök jägare eller jakt" value={q} onChange={e => setQ(e.target.value)}
        style={{ minHeight: 44, width: '100%', boxSizing: 'border-box', padding: '0 12px', borderRadius: RADIUS.sm, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontFamily: 'inherit', fontSize: 15 }} />
      <div>
        {shown.length === 0 && <div style={{ ...TYPE.small, color: T.textMuted, padding: '8px 0' }}>{list.length ? 'Inget att visa med det här filtret.' : 'Inga avgifter än. De skapas när jaktledaren bekräftar fällt vilt under Efter jakt.'}</div>}
        {shown.map(c => <ChargeRow key={c.id} c={c} onChanged={n => setList(l => l && l.map(x => (x.id === n.id ? n : x)))} />)}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {unpaid.length > 1 && <Button variant="secondary" disabled={remindingAll} onClick={remindAll}>Påminn alla obetalda</Button>}
        <Button variant="secondary" onClick={() => void exportCsv('lista')} icon={<Icon as={Download} size={18} />}>Exportera CSV</Button>
        <Button variant="secondary" onClick={() => void exportCsv('bokforing')} icon={<Icon as={Download} size={18} />}>Bokföringsunderlag</Button>
      </div>
      <div style={{ fontSize: 12, color: T.textMuted }}>Bokföringsunderlaget föreslår BAS-konton (kassa 1910, bank/Swish 1930, Stripe 1580, plattformsavgift 6570, fällavgifter 3010, böter 3990) – stäm av med din revisor, även momsen.</div>
    </Card>
  );
}

/** A6: viltslag som inte får fällas på marken, med bot. Tom bot = markens "Böter olovligt vilt". */
export function ForbiddenGamePanel({ markId, canEdit }: { markId: string; canEdit: boolean }) {
  const T = useTheme();
  const [rules, setRules] = useState<ForbiddenRule[] | null>(null);
  const [defaultFine, setDefaultFine] = useState(0);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    huntApi.markForbiddenGame(markId).then(g => { setRules(g.rules); setDefaultFine(g.defaultFineKr); }).catch(() => setRules([]));
  }, [markId]);
  if (!rules) return null;
  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const g = await huntApi.setMarkForbiddenGame(markId, rules.filter(r => r.viltslag.trim()).map(r => ({ viltslag: r.viltslag.trim(), kon: r.kon || null, bot: r.bot ?? null })));
      setRules(g.rules); setMsg('Sparat');
    } catch (e: any) { setMsg(e.message || 'Kunde inte spara'); }
    setSaving(false);
  };
  const set = (i: number, patch: Partial<ForbiddenRule>) => setRules(rs => rs && rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const field: React.CSSProperties = { minHeight: 44, padding: '0 10px', borderRadius: RADIUS.sm, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontFamily: 'inherit', fontSize: 15, minWidth: 0 };
  return (
    <Card data-testid="forbidden-game" style={{ marginBottom: 24, display: 'grid', gap: 12 }}>
      <div>
        <div style={{ ...TYPE.title }}>Ej tillåtet vilt och böter</div>
        <div style={{ ...TYPE.small, color: T.textSub }}>Fäller någon ett viltslag som marken inte tillåter varnar appen och boten läggs på jägarens summering efter jakt. Tom bot = "Böter olovligt vilt" ({kr(defaultFine)}).</div>
      </div>
      <datalist id={`forbidden-${markId}`}>{GAME_OPTIONS.map(g => <option key={g} value={g} />)}</datalist>
      {rules.length === 0 && <div style={{ ...TYPE.small, color: T.textMuted }}>Alla viltslag i jakttid är tillåtna.</div>}
      {rules.map((r, i) => (
        <div key={i} data-testid="forbidden-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr)) auto', gap: 8, alignItems: 'center' }}>
          <input aria-label={`Viltslag ${i + 1}`} list={`forbidden-${markId}`} placeholder="Viltslag" value={r.viltslag} disabled={!canEdit} onChange={e => set(i, { viltslag: e.target.value })} style={field} />
          <select aria-label={`Kön ${i + 1}`} value={r.kon || ''} disabled={!canEdit} onChange={e => set(i, { kon: e.target.value || null })} style={field}>
            <option value="">Alla kön</option><option>Hane</option><option>Hona</option>
          </select>
          <input aria-label={`Bot ${i + 1} (kr)`} type="number" min={0} inputMode="numeric" placeholder={`Bot (${defaultFine} kr)`} value={r.bot ?? ''} disabled={!canEdit}
            onChange={e => set(i, { bot: e.target.value === '' ? null : Number(e.target.value) })} style={field} />
          {canEdit && <IconButton label={`Ta bort ${r.viltslag || 'raden'}`} onClick={() => setRules(rs => rs && rs.filter((_, j) => j !== i))}><Icon as={Trash2} /></IconButton>}
        </div>
      ))}
      {canEdit && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="secondary" onClick={() => setRules(rs => [...(rs ?? []), { viltslag: '', kon: null, bot: null }])} icon={<Icon as={Plus} size={18} />}>Lägg till viltslag</Button>
        <Button onClick={save} disabled={saving}>Spara</Button>
        {msg && <span role="status" style={{ fontSize: 14, color: T.textSub }}>{msg}</span>}
      </div>}
    </Card>
  );
}
